import styled, { css } from 'styled-components'
import { pageCenter } from 'styles/page-center.styles'

export const Wrapper = styled.div`
  ${pageCenter};
`

export const Banner = styled.div`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary.light} 100%
  );
  border-radius: ${({ theme }) => theme.borderRadius[5]};
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  text-align: center;

  ${({ theme }) => css`
    ${theme.breakpoints.down.md} {
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    ${theme.breakpoints.down.sm} {
      padding: 1.25rem;
      margin-bottom: 1rem;
    }
  `}
`

export const BannerContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`

export const BannerTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;

  ${({ theme }) => css`
    ${theme.breakpoints.down.md} {
      font-size: 2rem;
    }

    ${theme.breakpoints.down.sm} {
      font-size: 1.75rem;
    }
  `}
`

export const BannerDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  opacity: 0.9;
  margin: 0;

  ${({ theme }) => css`
    ${theme.breakpoints.down.md} {
      font-size: 1rem;
    }

    ${theme.breakpoints.down.sm} {
      font-size: 0.875rem;
    }
  `}
`

export const LessonsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  max-width: 100%;

  ${({ theme }) => css`
    ${theme.breakpoints.down.lg} {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 1.25rem;
    }

    ${theme.breakpoints.down.md} {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1rem;
    }

    ${theme.breakpoints.down.sm} {
      grid-template-columns: 1fr;
      gap: 0.875rem;
    }
  `}
`

export const StartButton = styled.div<{ isAiLesson?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;

  button {
    cursor: pointer;
    background: transparent;
    height: 1.5rem;
    font-family: monospace;
    border: 1px solid ${({ theme }) => theme.palette.border.light};
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-radius: 50px;
    padding: 1.2rem 1.25rem;
    margin-left: 1rem;
    white-space: nowrap;

    &:hover {
      color: ${({ theme }) => theme.palette.primary.main}!important;
      background-color: ${({ theme }) => theme.palette.primary.contrastText}!important;
    }
  }

  ${({ theme }) => css`
    ${theme.breakpoints.down.md} {
      display: none;
    }
  `}
`

export const LessonCard = styled.div<{ isHighlighted?: boolean }>`
  height: 260px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  padding: 1.5rem;
  padding-top: 2.5rem;
  position: relative;
  color: ${({ theme }) => theme.palette.text.primary};
  border: 2px solid
    ${({ theme, isHighlighted }) => (isHighlighted ? theme.palette.primary.main : theme.palette.primary.main)};
  border-radius: ${({ theme }) => theme.borderRadius[5]};
  background: ${({ theme, isHighlighted }) =>
    isHighlighted
      ? `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}10 100%)`
      : theme.palette.background.paper};
  transition: all 0.3s ease-in-out;
  box-shadow: ${({ isHighlighted }) => (isHighlighted ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none')};

  &::before {
    content: ${({ isHighlighted }) => (isHighlighted ? '"✨ Featured"' : 'none')};
    position: absolute;
    top: -10px;
    left: 20px;
    background: ${({ theme }) => theme.palette.primary.main};
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    z-index: 1;
  }

  & ${StartButton} {
    width: 0px;
    transition: width 0.3s ease-in-out;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.primary.main};
    color: white !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    & * {
      color: white !important;
    }

    & ${StartButton} {
      width: 150px;
    }
  }

  ${({ theme }) => css`
    ${theme.breakpoints.down.md} {
      height: 220px;
      padding: 1.25rem;
      padding-top: 2.2rem;
    }

    ${theme.breakpoints.down.sm} {
      height: 200px;
      padding: 1rem;
      padding-top: 2rem;
    }
  `}
`

export const LessonCardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`

export const LessonCardHeader = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

export const LessonCardDescription = styled.p`
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin: 0.5rem 0 0 0;
  opacity: 0.8;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ theme }) => css`
    ${theme.breakpoints.down.sm} {
      font-size: 0.8rem;
      -webkit-line-clamp: 2;
    }
  `}
`

export const LessonCardStats = styled.div`
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  z-index: 2;
`

export const LessonStatPill = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  line-height: 1.2;
  letter-spacing: 0.01em;
`

export const LessonAccuracy = styled(LessonStatPill)``
export const LessonWpm = styled(LessonStatPill)``
